import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY } from '../lib/env';
import { log } from '../lib/logger';

export default function MoloniOAuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing Moloni authorization...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const success = params.get('success');
        const companyId = params.get('company_id');
        const error = params.get('error');

        log.debug('[MoloniCallback] Received params:', { success, companyId, error });

        if (error) {
          throw new Error(error);
        }

        if (success === 'true') {
          setStatus('success');
          setMessage(`Moloni connected successfully! Company ID: ${companyId}`);

          // Redirect back to the main app after 2 seconds
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 2000);
        } else {
          throw new Error('OAuth flow did not complete successfully');
        }

      } catch (error) {
        log.error('[MoloniCallback] Error:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Failed to connect Moloni');

        // Redirect back after 5 seconds even on error
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 5000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100-lg p-8 max-w-md w-full">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <RefreshCw className="w-16 h-16 mx-auto mb-4 text-blue-600 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Connecting Moloni</h2>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Failed</h2>
            </>
          )}

          <p className="text-gray-600">{message}</p>

          {status === 'error' && (
            <button
              onClick={() => navigate('/', { replace: true })}
              className="mt-6 px-6 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors hover:scale-[1.02]"
            >
              Return to Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
